import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const AdministrativeControls = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [auditFilter, setAuditFilter] = useState('all');

  const userManagement = [
    {
      id: 1,
      name: 'John Kamau',
      email: 'j.kamau@lawdesk.ke',
      role: 'Senior Associate',
      status: 'active',
      lastLogin: '2024-01-15 09:30',
      permissions: ['case-access', 'client-contact', 'document-edit']
    },
    {
      id: 2,
      name: 'Mary Wanjiku',
      email: 'm.wanjiku@lawdesk.ke',
      role: 'Junior Associate',
      status: 'active',
      lastLogin: '2024-01-15 14:22',
      permissions: ['case-view', 'document-view']
    },
    {
      id: 3,
      name: 'Peter Mwangi',
      email: 'p.mwangi@lawdesk.ke',
      role: 'Paralegal',
      status: 'inactive',
      lastLogin: '2024-01-12 16:45',
      permissions: ['research-access', 'document-draft']
    },
    {
      id: 4,
      name: 'Grace Akinyi',
      email: 'g.akinyi@lawdesk.ke',
      role: 'Legal Secretary',
      status: 'active',
      lastLogin: '2024-01-15 08:15',
      permissions: ['admin-support', 'scheduling']
    }
  ];

  const billingRates = [
    { role: 'Senior Partner', current: 25000, proposed: 27000, effective: '2024-02-01' },
    { role: 'Partner', current: 20000, proposed: 22000, effective: '2024-02-01' },
    { role: 'Senior Associate', current: 15000, proposed: 16500, effective: '2024-02-01' },
    { role: 'Junior Associate', current: 8500, proposed: 9200, effective: '2024-02-01' },
    { role: 'Paralegal', current: 4200, proposed: 4500, effective: '2024-02-01' }
  ];

  const policyUpdates = [
    {
      id: 1,
      policy: 'Client Confidentiality Protocol',
      lastUpdated: '2024-01-10',
      status: 'active',
      description: 'Enhanced security measures for sensitive client data'
    },
    {
      id: 2,
      policy: 'Remote Work Guidelines',
      lastUpdated: '2024-01-08',
      status: 'draft',
      description: 'Updated policies for hybrid work arrangements'
    },
    {
      id: 3,
      policy: 'Billing Procedures',
      lastUpdated: '2024-01-05',
      status: 'active',
      description: 'Standardized billing practices and time tracking'
    },
    {
      id: 4,
      policy: 'Professional Development',
      lastUpdated: '2024-01-02',
      status: 'review',
      description: 'Continuing education requirements and reimbursements'
    }
  ];

  const auditTrail = [
    {
      id: 1,
      timestamp: '2024-01-15 10:30:22',
      user: 'Senior Partner A',
      action: 'Financial Data Access',
      resource: 'Monthly Revenue Report',
      ip: '192.168.1.45',
      status: 'success'
    },
    {
      id: 2,
      timestamp: '2024-01-15 09:15:33',
      user: 'Partner C',
      action: 'Billing Rate Update',
      resource: 'Senior Associate Rates',
      ip: '192.168.1.67',
      status: 'success'
    },
    {
      id: 3,
      timestamp: '2024-01-15 08:45:12',
      user: 'Senior Partner B',
      action: 'User Permission Change',
      resource: 'Mary Wanjiku Profile',
      ip: '192.168.1.23',
      status: 'success'
    },
    {
      id: 4,
      timestamp: '2024-01-14 16:22:45',
      user: 'Partner D',
      action: 'Failed Login Attempt',
      resource: 'Partner Portal',
      ip: '203.45.67.89',
      status: 'failed'
    },
    {
      id: 5,
      timestamp: '2024-01-14 14:30:18',
      user: 'Senior Partner A',
      action: 'Policy Update',
      resource: 'Confidentiality Protocol',
      ip: '192.168.1.45',
      status: 'success'
    }
  ];

  const tabs = [
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'billing', label: 'Billing Rates', icon: 'DollarSign' },
    { id: 'policies', label: 'Practice Policies', icon: 'FileText' },
    { id: 'audit', label: 'Audit Trail', icon: 'Shield' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'inactive': return 'text-error';
      case 'draft': return 'text-warning-600';
      case 'review': return 'text-primary';
      case 'success': return 'text-success';
      case 'failed': return 'text-error';
      default: return 'text-text-secondary';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'active': return 'bg-success-100 text-success-700';
      case 'inactive': return 'bg-error-100 text-error-700';
      case 'draft': return 'bg-warning-100 text-warning-700';
      case 'review': return 'bg-primary-100 text-primary-700';
      case 'success': return 'bg-success-100 text-success-700';
      case 'failed': return 'bg-error-100 text-error-700';
      default: return 'bg-secondary-100 text-text-secondary';
    }
  };

  const getRateIncrease = (current, proposed) => {
    return ((proposed - current) / current * 100).toFixed(1);
  };

  const filteredAuditTrail = auditFilter === 'all' 
    ? auditTrail 
    : auditTrail?.filter(entry => entry?.status === auditFilter);

  return (
    <div className="bg-surface rounded-lg shadow-card border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error-50 rounded-lg flex items-center justify-center">
              <Icon name="ShieldAlert" size={24} className="text-error" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-text-primary">Administrative Controls</h2>
              <p className="text-sm text-text-secondary">Secure partner-level management functions</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 text-error">
            <Icon name="Lock" size={16} />
            <span className="text-sm font-medium">Restricted Access</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                activeTab === tab?.id
                  ? 'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="hidden sm:inline">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* User Management */}
        {activeTab === 'users' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-md font-semibold text-text-primary">User Account Management</h3>
              <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth">
                <Icon name="UserPlus" size={16} />
                <span>Add User</span>
              </button>
            </div>

            <div className="space-y-4">
              {userManagement?.map((user) => (
                <div key={user?.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-secondary-50 transition-smooth">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} color="white" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-text-primary">{user?.name}</h4>
                      <p className="text-xs text-text-secondary">{user?.email}</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-text-secondary">{user?.role}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusBg(user?.status)}`}>
                          {user?.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-xs text-text-secondary">Last Login</p>
                      <p className="text-xs text-text-primary">{user?.lastLogin}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth">
                        <Icon name="Edit" size={16} className="text-text-secondary" />
                      </button>
                      <button className="p-2 hover:bg-secondary-100 rounded-lg transition-smooth">
                        <Icon name="Settings" size={16} className="text-text-secondary" />
                      </button>
                      <button className="p-2 hover:bg-error-100 rounded-lg transition-smooth">
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Billing Rates */}
        {activeTab === 'billing' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-md font-semibold text-text-primary">Billing Rate Adjustments</h3>
              <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth">
                <Icon name="Save" size={16} />
                <span>Apply Changes</span>
              </button>
            </div>

            <div className="space-y-4">
              {billingRates?.map((rate, index) => (
                <div key={index} className="p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-text-primary">{rate?.role}</h4>
                    <span className="text-xs text-text-secondary">Effective: {rate?.effective}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-text-secondary">Current Rate</p>
                      <p className="text-sm font-semibold text-text-primary">KES {rate?.current?.toLocaleString()}/hr</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-secondary">Proposed Rate</p>
                      <p className="text-sm font-semibold text-primary">KES {rate?.proposed?.toLocaleString()}/hr</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-secondary">Increase</p>
                      <p className="text-sm font-semibold text-success">+{getRateIncrease(rate?.current, rate?.proposed)}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-warning-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="AlertTriangle" size={16} className="text-warning-600" />
                <h4 className="text-sm font-semibold text-warning-600">Rate Change Impact</h4>
              </div>
              <p className="text-sm text-warning-700">
                Proposed rate increases will affect all new engagements from the effective date. 
                Existing clients may require contract amendments.
              </p>
            </div>
          </div>
        )}

        {/* Practice Policies */}
        {activeTab === 'policies' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-md font-semibold text-text-primary">Practice Policy Management</h3>
              <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth">
                <Icon name="Plus" size={16} />
                <span>New Policy</span>
              </button>
            </div>

            <div className="space-y-4">
              {policyUpdates?.map((policy) => (
                <div key={policy?.id} className="p-4 border border-border rounded-lg hover:bg-secondary-50 transition-smooth">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-text-primary">{policy?.policy}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusBg(policy?.status)}`}>
                        {policy?.status}
                      </span>
                      <button className="p-1 hover:bg-secondary-100 rounded transition-smooth">
                        <Icon name="MoreHorizontal" size={16} className="text-text-secondary" />
                      </button>
                    </div>
                  </div>

                  <p className="text-sm text-text-secondary mb-3">{policy?.description}</p>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-secondary">Last updated: {policy?.lastUpdated}</span>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center space-x-1 px-2 py-1 text-xs text-primary hover:bg-primary-50 rounded transition-smooth">
                        <Icon name="Eye" size={12} />
                        <span>View</span>
                      </button>
                      <button className="flex items-center space-x-1 px-2 py-1 text-xs text-primary hover:bg-primary-50 rounded transition-smooth">
                        <Icon name="Edit" size={12} />
                        <span>Edit</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Trail */}
        {activeTab === 'audit' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-md font-semibold text-text-primary">Security Audit Trail</h3>
              <select 
                value={auditFilter}
                onChange={(e) => setAuditFilter(e.target?.value)}
                className="bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Actions</option>
                <option value="success">Successful</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="space-y-2">
              {filteredAuditTrail?.map((entry) => (
                <div key={entry?.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-secondary-50 transition-smooth">
                  <div className="flex items-center space-x-4">
                    <div className={`w-2 h-2 rounded-full ${
                      entry?.status === 'success' ? 'bg-success' : 'bg-error'
                    }`}></div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{entry?.action}</p>
                      <p className="text-xs text-text-secondary">{entry?.resource}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 text-xs text-text-secondary">
                    <span>{entry?.user}</span>
                    <span>{entry?.ip}</span>
                    <span>{entry?.timestamp}</span>
                    <span className={`px-2 py-1 rounded-full ${getStatusBg(entry?.status)}`}>
                      {entry?.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-info-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Info" size={16} className="text-info-600" />
                <h4 className="text-sm font-semibold text-info-600">Audit Information</h4>
              </div>
              <p className="text-sm text-info-700">
                All partner portal activities are logged for security compliance. 
                Audit records are retained for 7 years and cannot be modified.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdministrativeControls;